/**
 * ProjectDeliverable - Profesionálna výstupná dokumentácia pre odberateľa/investora
 * Obsahuje: úvodná strana, sumár, mapa pokrytia, zákazníci, parametre, schéma, certifikáty
 */
import React, { useState, useEffect } from 'react';
import { useProjectStore, usePoleStore, useCableStore } from '../../stores';
import { useONUStore } from '../../stores/onuStore';
import './ProjectDeliverable.css';

export const ProjectDeliverable: React.FC = () => {
  const { currentProject } = useProjectStore();
  const { poles, fetchPoles } = usePoleStore();
  const { cables, fetchCables } = useCableStore();
  const { onus, fetchONUs } = useONUStore();
  const [activeSection, setActiveSection] = useState<string>('cover');
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  useEffect(() => {
    if (currentProject) {
      fetchPoles(currentProject.id);
      fetchCables(currentProject.id);
      fetchONUs(currentProject.id);
    }
  }, [currentProject, fetchPoles, fetchCables, fetchONUs]);

  // Statistics
  const totalPoles = poles.length;
  const totalCables = cables.length;
  const totalLength = cables.reduce((sum, c) => {
    const length = c.length_meters ? (typeof c.length_meters === 'number' ? c.length_meters : parseFloat(String(c.length_meters))) : 0;
    return sum + length;
  }, 0);
  const polesWithCassettes = poles.filter((p) => p.cassette?.has_cassette);
  const totalSplitters = polesWithCassettes.reduce((sum, p) => sum + (p.cassette?.splitters?.length || 0), 0);
  const totalONUs = onus.length;
  const activeONUs = onus.filter((o) => {
    if (!o.signal_strength) return false;
    const signal = typeof o.signal_strength === 'string' ? parseFloat(o.signal_strength) : o.signal_strength;
    return signal > -28;
  }).length;
  const totalCustomers = new Set(onus.map(o => o.customer_name).filter(Boolean)).size;

  // Calculate total capacity
  const totalSplitterPorts = polesWithCassettes.reduce((sum, p) => {
    return sum + (p.cassette?.splitters?.reduce((s: number, splitter: any) => {
      const ratio = splitter.type.split(':')[1];
      return s + parseInt(ratio);
    }, 0) || 0);
  }, 0);

  const utilizationRate = totalSplitterPorts > 0 ? (totalONUs / totalSplitterPorts * 100) : 0;

  const handlePrint = () => {
    setIsPrinting(true);
    // Give React time to re-render all sections before opening print dialog
    setTimeout(() => {
      window.print();
      // Reset after print dialog closes
      setTimeout(() => setIsPrinting(false), 500);
    }, 300);
  };

  const shouldShowSection = (section: string) => {
    return isPrinting || activeSection === section;
  };

  const currentDate = new Date().toLocaleDateString('sk-SK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="project-deliverable">
      {/* Header - No Print */}
      <div className="deliverable-header no-print">
        <div className="deliverable-title">
          <h1>📊 Výstupná dokumentácia projektu</h1>
          <p>Profesionálna dokumentácia pre odberateľa/investora</p>
        </div>
        <button className="btn-print" onClick={handlePrint}>
          🖨️ Tlačiť
        </button>
      </div>

      {/* Navigation - No Print */}
      <div className="deliverable-nav no-print">
        <button
          className={activeSection === 'cover' ? 'active' : ''}
          onClick={() => setActiveSection('cover')}
        >
          📄 Úvodná strana
        </button>
        <button
          className={activeSection === 'summary' ? 'active' : ''}
          onClick={() => setActiveSection('summary')}
        >
          📊 Sumár projektu
        </button>
        <button
          className={activeSection === 'technical' ? 'active' : ''}
          onClick={() => setActiveSection('technical')}
        >
          ⚙️ Technické parametre
        </button>
        <button
          className={activeSection === 'customers' ? 'active' : ''}
          onClick={() => setActiveSection('customers')}
        >
          👥 Zákazníci
        </button>
        <button
          className={activeSection === 'warranty' ? 'active' : ''}
          onClick={() => setActiveSection('warranty')}
        >
          📜 Záručné podmienky
        </button>
      </div>

      {/* Content */}
      <div className="deliverable-content">
        {/* Cover Page */}
        {shouldShowSection('cover') && (
          <section className="deliverable-section cover-page">
            <div className="cover-content">
              <div className="cover-logo">
                <div className="logo-circle">
                  <span className="logo-icon">🌐</span>
                </div>
              </div>
              <h1 className="cover-title">GPON OPTICKÁ SIEŤ</h1>
              <h2 className="cover-project-name">{currentProject?.name}</h2>
              <div className="cover-divider"></div>
              <div className="cover-info">
                <div className="cover-info-item">
                  <span className="cover-label">Výstupná dokumentácia projektu</span>
                </div>
                <div className="cover-info-item">
                  <span className="cover-label">Dátum odovzdania:</span>
                  <span className="cover-value">{currentDate}</span>
                </div>
                <div className="cover-info-item">
                  <span className="cover-label">Zhotoviteľ:</span>
                  <span className="cover-value">GPON Network Solutions</span>
                </div>
              </div>
              <div className="cover-footer">
                <p>Tento dokument obsahuje kompletnú dokumentáciu realizovaného projektu</p>
                <p>optickej siete GPON vrátane technických parametrov a záručných podmienok.</p>
              </div>
            </div>
          </section>
        )}

        {/* Summary Section */}
        {shouldShowSection('summary') && (
          <section className="deliverable-section">
            <h2 className="section-title">📊 SUMÁR PROJEKTU</h2>
            
            <div className="summary-grid">
              <div className="summary-card primary">
                <div className="summary-icon">🏗️</div>
                <div className="summary-content">
                  <h3>Rozsah projektu</h3>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="stat-number">{totalPoles}</span>
                      <span className="stat-label">Stĺpikov</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{totalCables}</span>
                      <span className="stat-label">Káblov</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{(totalLength / 1000).toFixed(2)} km</span>
                      <span className="stat-label">Celková dĺžka</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="summary-card success">
                <div className="summary-icon">👥</div>
                <div className="summary-content">
                  <h3>Pripojení zákazníci</h3>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="stat-number">{totalCustomers}</span>
                      <span className="stat-label">Zákazníkov</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{totalONUs}</span>
                      <span className="stat-label">ONU zariadení</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{activeONUs}</span>
                      <span className="stat-label">Aktívnych</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="summary-card info">
                <div className="summary-icon">⚙️</div>
                <div className="summary-content">
                  <h3>Infraštruktúra</h3>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="stat-number">{polesWithCassettes.length}</span>
                      <span className="stat-label">Kaziet</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{totalSplitters}</span>
                      <span className="stat-label">Splitterov</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{totalSplitterPorts}</span>
                      <span className="stat-label">Portov</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="summary-card warning">
                <div className="summary-icon">📈</div>
                <div className="summary-content">
                  <h3>Využitie kapacity</h3>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="stat-number">{utilizationRate.toFixed(1)}%</span>
                      <span className="stat-label">Obsadenosť</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{totalSplitterPorts - totalONUs}</span>
                      <span className="stat-label">Voľných portov</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-number">{totalSplitterPorts}</span>
                      <span className="stat-label">Celková kapacita</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="project-description">
              <h3>Popis projektu</h3>
              <p>
                Projekt <strong>{currentProject?.name}</strong> predstavuje komplexnú realizáciu optickej siete GPON
                (Gigabit Passive Optical Network) s celkovou dĺžkou <strong>{(totalLength / 1000).toFixed(2)} km</strong> optických káblov.
              </p>
              <p>
                Sieť je navrhnutá pre pripojenie <strong>{totalCustomers} zákazníkov</strong> s celkovou kapacitou
                <strong> {totalSplitterPorts} koncových bodov</strong>. Aktuálne je pripojených <strong>{totalONUs} ONU zariadení</strong>,
                z čoho je <strong>{activeONUs} aktívnych</strong> s dobrým signálom.
              </p>
              <p>
                Infraštruktúra zahŕňa <strong>{totalPoles} stĺpikov</strong>, z toho <strong>{polesWithCassettes.length}</strong> s
                nainštalovanými kazetami obsahujúcimi <strong>{totalSplitters} splitterov</strong> rôznych typov.
              </p>
            </div>
          </section>
        )}

        {/* Technical Parameters Section */}
        {shouldShowSection('technical') && (
          <section className="deliverable-section">
            <h2 className="section-title">⚙️ TECHNICKÉ PARAMETRE</h2>
            
            <div className="tech-params">
              <div className="param-group">
                <h3>🌐 Technológia</h3>
                <table className="param-table">
                  <tbody>
                    <tr>
                      <td className="param-label">Typ siete:</td>
                      <td className="param-value">GPON (Gigabit Passive Optical Network)</td>
                    </tr>
                    <tr>
                      <td className="param-label">Štandard:</td>
                      <td className="param-value">ITU-T G.984 / G.988</td>
                    </tr>
                    <tr>
                      <td className="param-label">Downstream:</td>
                      <td className="param-value">2.488 Gbps (1490 nm)</td>
                    </tr>
                    <tr>
                      <td className="param-label">Upstream:</td>
                      <td className="param-value">1.244 Gbps (1310 nm)</td>
                    </tr>
                    <tr>
                      <td className="param-label">Max. vzdialenosť:</td>
                      <td className="param-value">20 km</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="param-group">
                <h3>🔌 Optické káble</h3>
                <table className="param-table">
                  <tbody>
                    <tr>
                      <td className="param-label">Celková dĺžka:</td>
                      <td className="param-value">{(totalLength / 1000).toFixed(2)} km</td>
                    </tr>
                    <tr>
                      <td className="param-label">Počet káblov:</td>
                      <td className="param-value">{totalCables}</td>
                    </tr>
                    <tr>
                      <td className="param-label">Útlm vlákna:</td>
                      <td className="param-value">0.35 dB/km @ 1550 nm</td>
                    </tr>
                    <tr>
                      <td className="param-label">Typ vlákna:</td>
                      <td className="param-value">Single-mode G.652.D</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="param-group">
                <h3>🔀 Pasívne prvky</h3>
                <table className="param-table">
                  <tbody>
                    <tr>
                      <td className="param-label">Počet splitterov:</td>
                      <td className="param-value">{totalSplitters}</td>
                    </tr>
                    <tr>
                      <td className="param-label">Celková kapacita:</td>
                      <td className="param-value">{totalSplitterPorts} portov</td>
                    </tr>
                    <tr>
                      <td className="param-label">Útlm zvaru:</td>
                      <td className="param-value">≤ 0.03 dB</td>
                    </tr>
                    <tr>
                      <td className="param-label">Útlm konektora:</td>
                      <td className="param-value">≤ 0.25 dB</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="param-group">
                <h3>📡 Koncové zariadenia</h3>
                <table className="param-table">
                  <tbody>
                    <tr>
                      <td className="param-label">Počet ONU:</td>
                      <td className="param-value">{totalONUs}</td>
                    </tr>
                    <tr>
                      <td className="param-label">Aktívne ONU:</td>
                      <td className="param-value">{activeONUs}</td>
                    </tr>
                    <tr>
                      <td className="param-label">Typ ONU:</td>
                      <td className="param-value">GPON ONT (1GE + WiFi)</td>
                    </tr>
                    <tr>
                      <td className="param-label">Optický výkon RX:</td>
                      <td className="param-value">-28 dBm až -8 dBm</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Customers Section */}
        {shouldShowSection('customers') && (
          <section className="deliverable-section">
            <h2 className="section-title">👥 ZOZNAM PRIPOJENÝCH ZÁKAZNÍKOV</h2>

            <div className="customers-summary">
              <div className="customer-stat-box">
                <span className="customer-stat-number">{totalCustomers}</span>
                <span className="customer-stat-label">Celkový počet zákazníkov</span>
              </div>
              <div className="customer-stat-box">
                <span className="customer-stat-number">{totalONUs}</span>
                <span className="customer-stat-label">Pripojených ONU</span>
              </div>
              <div className="customer-stat-box">
                <span className="customer-stat-number">{activeONUs}</span>
                <span className="customer-stat-label">Aktívnych pripojení</span>
              </div>
            </div>

            <table className="customers-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Zákazník</th>
                  <th>Adresa</th>
                  <th>Lokalita</th>
                  <th>Signál (dBm)</th>
                  <th>Stav</th>
                </tr>
              </thead>
              <tbody>
                {onus.map((onu, idx) => {
                  const signal = onu.signal_strength
                    ? (typeof onu.signal_strength === 'string' ? parseFloat(onu.signal_strength) : onu.signal_strength)
                    : null;
                  const status = signal && signal > -28 ? 'Aktívne' : signal ? 'Slabý signál' : 'Offline';
                  const statusClass = signal && signal > -28 ? 'status-active' : signal ? 'status-warning' : 'status-offline';
                  const pole = poles.find(p => p.id === onu.pole_id);

                  return (
                    <tr key={onu.id}>
                      <td>{idx + 1}</td>
                      <td><strong>{onu.customer_name || onu.name}</strong></td>
                      <td>{onu.notes || '-'}</td>
                      <td>{pole?.name || '-'}</td>
                      <td>{onu.signal_strength || '-'}</td>
                      <td><span className={`status-badge ${statusClass}`}>{status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {/* Warranty Section */}
        {shouldShowSection('warranty') && (
          <section className="deliverable-section">
            <h2 className="section-title">📜 ZÁRUČNÉ PODMIENKY A CERTIFIKÁCIA</h2>

            <div className="warranty-content">
              <div className="warranty-box">
                <h3>🛡️ Záruka</h3>
                <p>
                  Na všetky inštalované komponenty a vykonanú prácu poskytujeme záruku v trvaní <strong>24 mesiacov</strong>
                  od dátumu odovzdania projektu.
                </p>
                <ul>
                  <li>Optické káble: 25 rokov (záruka výrobcu)</li>
                  <li>Splittre a pasívne prvky: 20 rokov</li>
                  <li>ONU zariadenia: 24 mesiacov</li>
                  <li>Zvary a konektory: 24 mesiacov</li>
                  <li>Inštalačné práce: 24 mesiacov</li>
                </ul>
              </div>

              <div className="warranty-box">
                <h3>✅ Certifikácia a testy</h3>
                <p>
                  Všetky komponenty spĺňajú medzinárodné štandardy a boli testované podľa noriem:
                </p>
                <ul>
                  <li>ITU-T G.984 - GPON štandard</li>
                  <li>ITU-T G.652.D - Single-mode vlákna</li>
                  <li>IEC 61300 - Optické konektory</li>
                  <li>IEC 60794 - Optické káble</li>
                </ul>
              </div>

              <div className="warranty-box">
                <h3>🔧 Servis a podpora</h3>
                <p>
                  Poskytujeme komplexný servis a technickú podporu:
                </p>
                <ul>
                  <li>24/7 technická podpora (telefón, email)</li>
                  <li>Zásah technika do 24 hodín</li>
                  <li>Pravidelná údržba a kontrola siete</li>
                  <li>Monitoring kvality signálu</li>
                  <li>Bezplatné aktualizácie firmware ONU</li>
                </ul>
              </div>

              <div className="warranty-box">
                <h3>📋 Odovzdanie projektu</h3>
                <p>
                  Projekt bol odovzdaný dňa <strong>{currentDate}</strong> v nasledujúcom rozsahu:
                </p>
                <ul>
                  <li>Kompletná technická dokumentácia</li>
                  <li>As-built výkresy a schémy</li>
                  <li>Protokoly o meraniach OTDR</li>
                  <li>Zoznam všetkých komponentov</li>
                  <li>Návod na obsluhu a údržbu</li>
                  <li>Kontaktné údaje na servis</li>
                </ul>
              </div>

              <div className="warranty-box">
                <h3>⚠️ Vylúčenie zo záruky</h3>
                <p>
                  Záruka sa nevzťahuje na:
                </p>
                <ul>
                  <li>Mechanické poškodenie spôsobené treťou stranou</li>
                  <li>Poškodenie živelnou pohromou</li>
                  <li>Neoprávnené zásahy do siete</li>
                  <li>Nedodržanie prevádzkových podmienok</li>
                  <li>Bežné opotrebenie komponentov</li>
                </ul>
              </div>

              <div className="warranty-box">
                <h3>📞 Kontakt</h3>
                <p>
                  Pre servisné požiadavky a technickú podporu nás kontaktujte:
                </p>
                <ul>
                  <li><strong>Telefón:</strong> +421 XXX XXX XXX</li>
                  <li><strong>Email:</strong> support@gpon-network.sk</li>
                  <li><strong>Web:</strong> www.gpon-network.sk</li>
                  <li><strong>Pracovný čas:</strong> Po-Pia 8:00-16:00</li>
                  <li><strong>Pohotovosť:</strong> 24/7</li>
                </ul>
              </div>
            </div>

            <div className="signature-section">
              <div className="signature-box">
                <div className="signature-line"></div>
                <p className="signature-label">Podpis zhotoviteľa</p>
                <p className="signature-date">Dátum: {currentDate}</p>
              </div>
              <div className="signature-box">
                <div className="signature-line"></div>
                <p className="signature-label">Podpis odberateľa</p>
                <p className="signature-date">Dátum: _______________</p>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
